import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { visibleBlogPosts, getBlogPostBySlug, formatBlogDate } from "@/config/blog"
import { BlogDisclaimer } from "@/components/BlogDisclaimer"

const BASE_URL = "https://welephant.xyz"

/**
 * Static blog page for SEO crawlers.
 * This page is pre-rendered at build time and contains the full markdown content
 * for search engine indexing. Users are directed to the interactive version.
 */
export function StaticBlogPage() {
  const { slug } = useParams<{ slug: string }>()
  const [content, setContent] = useState<string>("")
  const [loading, setLoading] = useState(true)

  const currentPost = slug ? getBlogPostBySlug(slug) : visibleBlogPosts[0]
  const actualPost = currentPost || visibleBlogPosts[0]

  useEffect(() => {
    const fetchMarkdown = async () => {
      setLoading(true)
      try {
        const response = await fetch(actualPost.path)
        if (!response.ok) {
          throw new Error(`Failed to fetch ${actualPost.path}`)
        }
        const text = await response.text()
        setContent(text)
      } catch (error) {
        console.error("Error loading markdown:", error)
        setContent("# Error\n\nFailed to load blog post.")
      } finally {
        setLoading(false)
      }
    }

    fetchMarkdown()
  }, [actualPost.path])

  const fullTitle = `${actualPost.title} | WELEPHANT Blog`
  const fullUrl = `${BASE_URL}/static/blog/${actualPost.slug}`
  const canonicalUrl = `${BASE_URL}/blog/${actualPost.slug}`

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="title" content={fullTitle} />
        <meta name="description" content={actualPost.description} />
        <meta name="keywords" content={`${actualPost.title.toLowerCase()}, WELEPHANT blog, prediction mining, blockchain gaming`} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content={actualPost.author} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={actualPost.description} />
        <meta property="og:image" content={actualPost.coverImage ? `${BASE_URL}${actualPost.coverImage}` : `${BASE_URL}/og-image.png`} />
        <meta property="og:site_name" content="WELEPHANT Blog" />
        <meta property="article:published_time" content={actualPost.date} />
        <meta property="article:author" content={actualPost.author} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={fullUrl} />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={actualPost.description} />
        <meta name="twitter:image" content={actualPost.coverImage ? `${BASE_URL}${actualPost.coverImage}` : `${BASE_URL}/og-image.png`} />

        {/* Canonical points to the interactive version */}
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <div style={{
        fontFamily: 'system-ui, -apple-system, sans-serif',
        maxWidth: '800px',
        margin: '0 auto',
        padding: '2rem',
        lineHeight: 1.6
      }}>
        {/* Navigation banner linking to interactive version */}
        <nav style={{
          backgroundColor: '#f5f5f5',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}>
            You're viewing a static version of this blog post.
          </p>
          <Link
            to={`/blog/${actualPost.slug}`}
            style={{
              color: '#0066cc',
              textDecoration: 'underline',
              fontWeight: 500
            }}
          >
            View "{actualPost.title}" in the interactive app
          </Link>
        </nav>

        {/* Blog navigation */}
        <nav style={{ marginBottom: '2rem' }}>
          <strong>Blog Posts:</strong>{' '}
          {visibleBlogPosts.map((post, index) => (
            <span key={post.slug}>
              {index > 0 && ' | '}
              <Link
                to={`/static/blog/${post.slug}`}
                style={{
                  color: post.slug === actualPost.slug ? '#333' : '#0066cc',
                  fontWeight: post.slug === actualPost.slug ? 600 : 400,
                  textDecoration: post.slug === actualPost.slug ? 'none' : 'underline'
                }}
              >
                {post.title}
              </Link>
            </span>
          ))}
        </nav>

        {/* Cover Image */}
        {actualPost.coverImage && (
          <img
            src={actualPost.coverImage}
            alt={actualPost.title}
            style={{
              width: '100%',
              aspectRatio: '16/9',
              objectFit: 'cover',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}
          />
        )}

        {/* Post Meta */}
        <div style={{
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid #eee',
          color: '#666',
          fontSize: '0.9rem'
        }}>
          <time dateTime={actualPost.date}>{formatBlogDate(actualPost.date)}</time>
          {' | '}
          <span>{actualPost.author}</span>
        </div>

        {/* Content */}
        <main>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Loading...
            </div>
          ) : (
            <article>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => (
                    <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '1rem' }}>
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.75rem' }}>
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p style={{ marginBottom: '1rem', color: '#444' }}>
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem', color: '#444' }}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem', color: '#444' }}>
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li style={{ marginBottom: '0.5rem' }}>{children}</li>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote style={{ borderLeft: '4px solid #ddd', paddingLeft: '1rem', fontStyle: 'italic', color: '#666', margin: '1rem 0' }}>
                      {children}
                    </blockquote>
                  ),
                  code: ({ className, children }) => {
                    const isInline = !className
                    return isInline ? (
                      <code style={{ backgroundColor: '#f5f5f5', padding: '0.2rem 0.4rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.9em' }}>
                        {children}
                      </code>
                    ) : (
                      <code style={{ display: 'block', backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.9em', overflow: 'auto', margin: '1rem 0' }}>
                        {children}
                      </code>
                    )
                  },
                  pre: ({ children }) => (
                    <pre style={{ backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '8px', overflow: 'auto', margin: '1rem 0' }}>
                      {children}
                    </pre>
                  ),
                  table: ({ children }) => (
                    <div style={{ overflow: 'auto', margin: '1rem 0' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        {children}
                      </table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: 600 }}>
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td style={{ border: '1px solid #ddd', padding: '0.75rem', color: '#444' }}>
                      {children}
                    </td>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      style={{ color: '#0066cc', textDecoration: 'underline' }}
                      target={href?.startsWith("http") ? "_blank" : undefined}
                      rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
                    >
                      {children}
                    </a>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ fontWeight: 600 }}>{children}</strong>
                  ),
                  img: ({ src, alt }) => (
                    <img
                      src={src}
                      alt={alt || ""}
                      style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', margin: '1rem 0' }}
                    />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </article>
          )}

          <BlogDisclaimer />
        </main>

        {/* Footer with link back to main site */}
        <footer style={{
          marginTop: '3rem',
          paddingTop: '2rem',
          borderTop: '1px solid #eee',
          textAlign: 'center',
          color: '#666'
        }}>
          <p>
            <Link to="/" style={{ color: '#0066cc', textDecoration: 'underline' }}>
              Visit WELEPHANT
            </Link>
            {' | '}
            <Link to="/blog" style={{ color: '#0066cc', textDecoration: 'underline' }}>
              All Blog Posts
            </Link>
            {' | '}
            <Link to={`/blog/${actualPost.slug}`} style={{ color: '#0066cc', textDecoration: 'underline' }}>
              View in App
            </Link>
          </p>
        </footer>
      </div>
    </>
  )
}
